// The pieces the CRM pages share (D-163): the follow-up form (five kinds, a day, a time or any time, who takes it),
// what can be done to a follow-up, her Hot/Warm/Cold label, the stage with why a customer was lost, and WhatsApp.
import { type FormEvent, useState } from "react";
import {
  dayChoices,
  dueAt,
  dueWords,
  filledMessage,
  FOLLOW_UP_KINDS,
  type FollowUpKind,
  followUpKind,
  followUpLabel,
  type FollowUpType,
  isDueOver,
  isVisit,
  type Label,
  labelName,
  LABELS,
  LOST_REASONS,
  type MessageDetails,
  type MessageTemplate,
  snoozeChoices,
  type StageRef,
  TIME_CHOICES,
  VISIT_RESULTS,
  type VisitResult,
  whatsappLink,
} from "../lib/crm";
import { localDate, toWallTime } from "../lib/format";
import { DEFAULT_VISIT_MESSAGE } from "../lib/visits";
import type { Member } from "../lib/types";
import { useMember } from "./session";
import { ErrorNote, Field, Loading, Modal, useAction, useLoad } from "./ui";

/** A follow-up's kind as a small badge: "Visit", "Send documents". */
export function KindBadge({ type }: { type: FollowUpType | string }) {
  return <span className={`badge kind kind-${followUpKind(type)}`}>{followUpLabel(type)}</span>;
}

/** When it is due, red once it is overdue. */
export function Due(props: { dueAt: string; hasTime: boolean; tz: string }) {
  const over = isDueOver(props.dueAt, props.hasTime, props.tz);
  return (
    <strong className={over ? "overdue nowrap" : "nowrap"}>{dueWords(props.dueAt, props.hasTime, props.tz)}</strong>
  );
}

/** Her label as three toggles: tapping the chosen one again removes it. */
export function LabelPicker(props: { label: Label | null; busy?: boolean; onChange: (label: Label | null) => void }) {
  return (
    <div className="segmented small" role="group" aria-label="Label">
      {LABELS.map((l) => (
        <button
          key={l}
          type="button"
          className={props.label === l ? `on label-${l}` : ""}
          aria-pressed={props.label === l}
          disabled={props.busy}
          onClick={() => props.onChange(props.label === l ? null : l)}
        >
          {labelName[l]}
        </button>
      ))}
    </div>
  );
}

export function LabelBadge({ label }: { label: Label | null }) {
  return label ? <span className={`badge ${label}`}>{labelName[label]}</span> : null;
}

export function StageTag({ stage }: { stage: Pick<StageRef, "name" | "kind"> | null }) {
  if (!stage) return <span className="faint">–</span>;
  return (
    <span className={`badge ${stage.kind === "won" ? "ok" : stage.kind === "lost" ? "" : "brand"}`}>{stage.name}</span>
  );
}

/** The stage, as a list; a stage that loses the customer asks why first. */
export function StagePicker(props: {
  stages: StageRef[];
  stageId: string | null;
  busy?: boolean;
  onChange: (stageId: string, reason: string | null) => Promise<void>;
}) {
  const [asking, setAsking] = useState<StageRef | null>(null);
  const open = props.stages.filter((s) => s.kind === "open");
  const closed = props.stages.filter((s) => s.kind !== "open");
  return (
    <>
      <select
        aria-label="Stage"
        value={props.stageId ?? ""}
        disabled={props.busy}
        onChange={(e) => {
          const stage = props.stages.find((s) => s.id === e.target.value);
          if (!stage) return;
          if (stage.kind === "lost") setAsking(stage);
          else void props.onChange(stage.id, null);
        }}
      >
        {!props.stageId && <option value="">Choose a stage</option>}
        <optgroup label="Open">
          {open.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </optgroup>
        <optgroup label="Closed">
          {closed.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </optgroup>
      </select>
      {asking && (
        <LostModal
          stage={asking}
          onClose={() => setAsking(null)}
          onSave={async (reason) => {
            await props.onChange(asking.id, reason);
            setAsking(null);
          }}
        />
      )}
    </>
  );
}

function LostModal(props: { stage: StageRef; onClose: () => void; onSave: (reason: string | null) => Promise<void> }) {
  const [reason, setReason] = useState<string | null>(null);
  const [other, setOther] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await props.onSave(reason === "Other" ? (other.trim() || "Other") : reason);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={`${props.stage.name}: why?`} onClose={props.onClose}>
      <form onSubmit={submit}>
        <p className="muted" style={{ marginTop: 0 }}>
          Their open follow-ups are cancelled. Why was it lost? (optional)
        </p>
        <div className="chips" role="radiogroup" aria-label="Why it was lost">
          {LOST_REASONS.map((r) => (
            <label key={r} className={`chip${reason === r ? " on" : ""}`}>
              <input type="radio" name="reason" checked={reason === r} onChange={() => setReason(r)} />
              {r}
            </label>
          ))}
        </div>
        {reason === "Other" && (
          <Field label="In a few words">
            <input value={other} maxLength={200} onChange={(e) => setOther(e.target.value)} autoFocus />
          </Field>
        )}
        <div className="actions">
          <button type="button" onClick={props.onClose}>Cancel</button>
          <button className="primary" disabled={busy}>Mark {props.stage.name.toLowerCase()}</button>
        </div>
      </form>
    </Modal>
  );
}

/** What the follow-up form starts from: an existing follow-up to change, or the kind to plan. */
export interface FollowUpStart {
  id?: string;
  type: FollowUpType | FollowUpKind;
  due_at?: string;
  due_has_time?: boolean;
  assigned_to?: string | null;
  place?: string | null;
}

/**
 * Plan a follow-up or change one: what (call, visit, meeting, send documents, collect payment), the day, a time or any
 * time, who takes it, where (a visit or meeting) and a note (a new one). As on the phone's follow-up sheet (C8).
 */
export function FollowUpModal(props: {
  title: string;
  leadId?: string;
  start: FollowUpStart;
  /** Shown when a new call (or send, or collect) will replace the open one (TASK-01). */
  replaces?: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { api, member, tz } = useMember();
  const members = useLoad(() => api.members(), [api]);
  const now = new Date();
  const days = dayChoices(now, tz, 6);
  const startDue = props.start.due_at ? new Date(props.start.due_at) : null;
  const startDay = startDue
    ? localDate(startDue, tz)
    : days[Number(toWallTime(now, tz).slice(11, 13)) >= 17 ? 1 : 0].day;
  const startTime = startDue && props.start.due_has_time !== false ? toWallTime(startDue, tz).slice(11, 16) : null;
  const editing = Boolean(props.start.id);
  const visits = member.visits ?? null;
  // A new visit given to nobody in particular goes to whoever handles the company's visits: the server applies the
  // rule (D-186), so "" here means "as the company decided".
  const byRule = !editing && visits !== null && visits.handler !== "booker";
  const whoFor = (k: FollowUpKind) => (byRule && k === "visit" ? "" : props.start.assigned_to ?? member.member_id);
  const [kind, setKind] = useState<FollowUpKind>(followUpKind(props.start.type));
  const [day, setDay] = useState(startDay);
  const [time, setTime] = useState<string | null>(props.start.id ? startTime : (startTime ?? "11:00"));
  const [who, setWho] = useState(
    editing ? props.start.assigned_to ?? member.member_id : whoFor(followUpKind(props.start.type)),
  );
  const [whoChosen, setWhoChosen] = useState(false);
  const [place, setPlace] = useState(props.start.place ?? "");
  const [reason, setReason] = useState("");
  const { busy, run } = useAction();
  // Shown once she has tried to save a time that has passed, and gone as soon as the time is fine.
  const [tried, setTried] = useState(false);
  const visit = kind === "visit" || kind === "meeting";
  const team = (members.data ?? []).filter((m: Member) => m.status === "active");
  const pastDay = day < localDate(now, tz);
  const due = dueAt(day, time, tz);
  const inPast = time !== null && Date.parse(due.due_at) < now.getTime() - 60_000;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (pastDay || inPast) {
      setTried(true);
      return;
    }
    const ok = await run(
      () =>
        api.saveFollowUp({
          id: props.start.id,
          lead_id: editing ? undefined : props.leadId,
          type: kind,
          due_at: due.due_at,
          due_has_time: due.due_has_time,
          assigned_to: who || null,
          place: visit ? place : null,
          reason: editing ? null : reason,
        }),
      editing ? "Follow-up changed" : `${FOLLOW_UP_KINDS.find((k) => k.kind === kind)!.label} planned`,
    );
    if (ok) props.onSaved();
  };

  return (
    <Modal title={props.title} onClose={props.onClose}>
      <form onSubmit={submit} className="follow-up-form">
        <Field label="What">
          <div className="chips" role="radiogroup" aria-label="What">
            {FOLLOW_UP_KINDS.map((k) => (
              <label key={k.kind} className={`chip${kind === k.kind ? " on" : ""}`}>
                <input
                  type="radio"
                  name="kind"
                  checked={kind === k.kind}
                  onChange={() => {
                    setKind(k.kind);
                    if (!editing && !whoChosen) setWho(whoFor(k.kind));
                  }}
                />
                {k.label}
              </label>
            ))}
          </div>
        </Field>
        {!editing && !visit && props.replaces && <div className="hint-note">{props.replaces}</div>}
        <Field label="Day">
          <div className="chips" role="radiogroup" aria-label="Day">
            {days.map((d) => (
              <label key={d.day} className={`chip${day === d.day ? " on" : ""}`}>
                <input type="radio" name="day" checked={day === d.day} onChange={() => setDay(d.day)} />
                {d.label}
              </label>
            ))}
            <input
              type="date"
              aria-label="Another day"
              value={day}
              min={localDate(now, tz)}
              onChange={(e) => e.target.value && setDay(e.target.value)}
            />
          </div>
        </Field>
        <Field label="Time" hint={`Times in ${tz}`}>
          <div className="chips" role="radiogroup" aria-label="Time">
            {TIME_CHOICES.map((t) => (
              <label key={t} className={`chip${time === t ? " on" : ""}`}>
                <input type="radio" name="time" checked={time === t} onChange={() => setTime(t)} />
                {t}
              </label>
            ))}
            {!visit && (
              <label className={`chip${time === null ? " on" : ""}`}>
                <input type="radio" name="time" checked={time === null} onChange={() => setTime(null)} />
                Any time
              </label>
            )}
            <input
              type="time"
              aria-label="Another time"
              value={time ?? ""}
              onChange={(e) => setTime(e.target.value || null)}
            />
          </div>
        </Field>
        <Field label={visit ? "Who will take it" : "Who"}>
          <select
            value={who}
            onChange={(e) => {
              setWho(e.target.value);
              setWhoChosen(true);
            }}
          >
            {byRule && kind === "visit" && (
              <option value="">
                {visits?.handler === "one"
                  ? `${visits.handler_member?.name ?? "Whoever handles visits"} (handles visits)`
                  : "Whoever is free then (in turn)"}
              </option>
            )}
            <option value={member.member_id}>Me ({member.display_name})</option>
            {team.filter((m) => m.id !== member.member_id).map((m) => (
              <option key={m.id} value={m.id}>{m.display_name}</option>
            ))}
          </select>
        </Field>
        {visit && (
          <Field
            label="Where (optional)"
            hint={kind === "visit" && visits?.place ? "Left empty, the company's place." : undefined}
          >
            <input
              value={place}
              maxLength={200}
              onChange={(e) => setPlace(e.target.value)}
              placeholder={kind === "visit" && visits?.place ? visits.place : "Site office"}
            />
          </Field>
        )}
        {!editing && (
          <Field label="Note (optional)">
            <input
              value={reason}
              maxLength={200}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Send the brochure first"
            />
          </Field>
        )}
        <ErrorNote
          error={tried && (pastDay || inPast)
            ? "Pick a time from now on."
            : visit && time === null
            ? "A visit or meeting needs a time."
            : null}
        />
        <div className="actions">
          <button type="button" onClick={props.onClose}>Cancel</button>
          <button className="primary" disabled={busy || (visit && time === null)}>
            {editing ? "Save" : "Plan it"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** Done (a visit or meeting says whether it happened), snooze, change and cancel: what she does to a follow-up. */
export function FollowUpActions(props: {
  followUp: { id: string; type: FollowUpType | string };
  onChange: () => void;
  onEdit: () => void;
  compact?: boolean;
}) {
  const { api, tz } = useMember();
  const { busy, run } = useAction();
  const [cancelling, setCancelling] = useState(false);
  const act = async (fn: () => Promise<void>, done: string) => {
    if (await run(fn, done)) props.onChange();
  };
  const visit = isVisit(props.followUp.type);
  return (
    <span className="row follow-up-actions">
      {visit
        ? (
          <select
            className="small"
            aria-label="Done"
            value=""
            disabled={busy}
            onChange={(e) =>
              e.target.value &&
              void act(() => api.completeFollowUp(props.followUp.id, e.target.value as VisitResult), "Marked done")}
          >
            <option value="">Done…</option>
            {VISIT_RESULTS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        )
        : (
          <button
            className="small primary"
            disabled={busy}
            onClick={() => void act(() => api.completeFollowUp(props.followUp.id), "Marked done")}
          >
            Done
          </button>
        )}
      <select
        className="small"
        aria-label="Snooze"
        value=""
        disabled={busy}
        onChange={(e) => {
          const choice = snoozeChoices(new Date(), tz).find((c) => c.key === e.target.value);
          if (choice) void act(() => api.snoozeFollowUp(props.followUp.id, choice.until), `Moved to ${choice.label}`);
        }}
      >
        <option value="">Snooze…</option>
        {snoozeChoices(new Date(), tz).map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
      </select>
      <button className="small" disabled={busy} onClick={props.onEdit}>Change</button>
      {!props.compact && (
        cancelling
          ? (
            <span className="row">
              <span className="faint">Cancel it?</span>
              <button
                className="small danger solid"
                disabled={busy}
                onClick={() => void act(() => api.cancelFollowUp(props.followUp.id), "Follow-up cancelled")}
              >
                Yes
              </button>
              <button className="small" onClick={() => setCancelling(false)}>No</button>
            </span>
          )
          : <button className="small ghost" disabled={busy} onClick={() => setCancelling(true)}>Cancel</button>
      )}
    </span>
  );
}

/** A visit's details for its message (D-187), and who it signs off as. */
export interface VisitMessage {
  details: MessageDetails;
  caller: string | null;
}

/** Offered for a visit when the company has marked none of its messages for visits. */
const STANDARD_VISIT_MESSAGE: MessageTemplate = {
  id: "",
  name: "Visit details",
  body: { en: DEFAULT_VISIT_MESSAGE },
  link: null,
  for_visits: true,
};

/**
 * WhatsApp with one of the company's messages filled in, or her own; kept in the customer's history. For a visit, its
 * messages for visits come first, the first of them chosen, with the visit's details filled in (D-187).
 */
export function WhatsAppModal(props: {
  leadId: string;
  phone: string;
  customerName: string | null;
  company?: string | null;
  visit?: VisitMessage;
  onClose: () => void;
  onSent: () => void;
}) {
  const { api, member } = useMember();
  const templates = useLoad(() => api.messageTemplates(), [api]);
  /** undefined: not chosen yet, so the first one for a visit, else her own. */
  const [picked, setPicked] = useState<MessageTemplate | null | undefined>(undefined);
  const { busy, run } = useAction();
  const all = templates.data ?? [];
  const forVisits = all.filter((t) => t.for_visits);
  const options = props.visit
    ? [...(forVisits.length ? forVisits : [STANDARD_VISIT_MESSAGE]), ...all.filter((t) => !t.for_visits)]
    : all;
  const chosen = picked === undefined ? (props.visit && templates.data ? options[0] : null) : picked;
  const details: MessageDetails = { company: props.company, ...props.visit?.details };
  const caller = props.visit ? props.visit.caller : member.display_name;
  const fill = (t: MessageTemplate) => filledMessage(t, member.language, props.customerName, caller, details);
  const text = chosen ? fill(chosen) : null;
  const send = async () => {
    // Open WhatsApp first, so the browser lets the new tab open; the message is then kept in the history.
    window.open(whatsappLink(props.phone, text), "_blank", "noopener");
    if (await run(() => api.logMessage(props.leadId, chosen?.id || null), "Kept in the history")) props.onSent();
  };
  return (
    <Modal title={props.visit ? "Send visit details" : "WhatsApp"} onClose={props.onClose}>
      {templates.loading && !templates.data && <Loading />}
      <ErrorNote error={templates.error} />
      <div className="list">
        {options.map((t) => (
          <label key={t.id || "standard"} className={`list-item template${chosen === t ? " on" : ""}`}>
            <input
              type="radio"
              name="template"
              checked={chosen === t}
              onChange={() =>
                setPicked(t)}
            />
            <span>
              <strong>{t.name}</strong>
              <span className="faint template-body">{fill(t)}</span>
            </span>
          </label>
        ))}
        <label className={`list-item template${chosen === null ? " on" : ""}`}>
          <input type="radio" name="template" checked={chosen === null} onChange={() => setPicked(null)} />
          <span>
            <strong>Write my own</strong>
            <span className="faint template-body">WhatsApp opens with an empty message.</span>
          </span>
        </label>
      </div>
      <div className="actions">
        <button type="button" onClick={props.onClose}>Cancel</button>
        <button className="primary" disabled={busy} onClick={() => void send()}>Open WhatsApp</button>
      </div>
    </Modal>
  );
}
